/**
 * Explicit target — the hostile the player clicked or tapped (docs/design/combat.md
 * §Control model, "Explicit target"; AD-817).
 *
 * The soft target (`softTarget.ts`) already reserved `explicitTargetId`: an explicit
 * target, while it is alive and inside the server's 26 m ability bound, wins over the
 * threat/nearest picks for the basic attack AND for every ability press (keys 1–6, the
 * ability bar), because both read the same picker. This module only holds the id and
 * applies the clearing rules; the picker stays the one place that decides.
 *
 * One shared holder (`explicitTarget`) because two readers need it — the Game's attack
 * loop and the store's `softTarget()` that the HUD's ability press calls — and neither
 * owns the other. Pure (no THREE, no DOM) so it runs under `node --test`.
 */

import type { HostileView } from './softTarget.js';

export class ExplicitTarget {
  private current: number | null = null;

  /** The held target's wire id, or null. */
  get id(): number | null {
    return this.current;
  }

  /** A click/tap on a hostile chose it. */
  set(id: number): void {
    this.current = id;
  }

  /** Ground click, keyboard move, zone swap, teardown. */
  clear(): void {
    this.current = null;
  }

  /**
   * Clear the target when it is no longer a live hostile among `hostiles` (it died,
   * despawned, or left interest). Returns whether a target is still held.
   */
  validate(hostiles: Iterable<HostileView>): boolean {
    if (this.current === null) return false;
    for (const h of hostiles) {
      if (h.id !== this.current) continue;
      if (h.health === undefined || h.health > 0) return true;
      break;
    }
    this.current = null;
    return false;
  }
}

/** The one holder the Game sets and the store's soft-target pick reads. */
export const explicitTarget = new ExplicitTarget();
